import React, { useState, useEffect, useMemo } from "react";
import { Typography, Box, Button, Paper, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { auth } from "../../firebase";
import { stripePromise } from "../../stripe";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import "./orderHistory.scss";

const flag = (value) => String(value || "").toLowerCase() === "true";

const STRIPE_TEST_MODE = flag(process.env.REACT_APP_STRIPE_TEST_MODE);
const USE_EMULATORS = flag(process.env.REACT_APP_USE_EMULATORS);
const FUNCTIONS_BASE_URL = (
  process.env.REACT_APP_FUNCTIONS_BASE_URL || ""
).replace(/\/$/, "");

const createEndpointList = (envUrl, suffix) => {
  const endpoints = [];
  if (USE_EMULATORS && FUNCTIONS_BASE_URL) {
    endpoints.push(`${FUNCTIONS_BASE_URL}/${suffix}`);
  }
  if (envUrl) {
    endpoints.push(envUrl);
  }
  return Array.from(new Set(endpoints.filter(Boolean)));
};

const PAYMENT_INTENT_ENDPOINTS = createEndpointList(
  process.env.REACT_APP_CREATE_PAYMENT_INTENT_URL,
  "createPaymentIntent"
);

const isEmulatorEndpoint = (url) =>
  USE_EMULATORS &&
  FUNCTIONS_BASE_URL &&
  typeof url === "string" &&
  url.startsWith(FUNCTIONS_BASE_URL);

export default function ArtHistory({ userOrders }) {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [imageDimensions, setImageDimensions] = useState({});
  const [previewBg, setPreviewBg] = useState("transparent");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortOption, setSortOption] = useState("desc"); // 'desc' | 'asc'
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [showStripeCheckout, setShowStripeCheckout] = useState(false);
  const [cardError, setCardError] = useState(null);
  const [cardMounted, setCardMounted] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);

  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);

  const orders = Array.isArray(userOrders) ? userOrders : [];

  const toDateSafe = (ts) => {
    if (!ts) return null;
    if (ts instanceof Date) return ts;
    if (typeof ts?.toDate === "function") {
      try {
        return ts.toDate();
      } catch {
        return null;
      }
    }
    if (
      typeof ts === "object" &&
      ts !== null &&
      typeof ts.seconds === "number"
    ) {
      return new Date(ts.seconds * 1000);
    }
    if (typeof ts === "number") {
      const ms = ts < 1e12 ? ts * 1000 : ts;
      return new Date(ms);
    }
    if (typeof ts === "string") {
      const d = new Date(ts);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  const formatDateTime = (val) => {
    const d = toDateSafe(val);
    if (!d) return "Unknown date";
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const formatMoney = (val) => {
    const n = typeof val === "string" ? Number(val) : Number(val ?? 0);
    const safe = Number.isFinite(n) ? n : 0;
    return safe.toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
    });
  };

  const statusKey = (status) =>
    String(status || "Pending")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-");

  const getOrderImages = (order) =>
    Array.isArray(order?.images) && order.images.length ? order.images : [];

  const sumQuantities = (items) => {
    if (!Array.isArray(items) || !items.length) return 0;
    return items.reduce((sum, it) => {
      if (typeof it === "string") return sum + 1;
      const q = Number(it?.quantity ?? it?.count ?? 1);
      return sum + (Number.isFinite(q) && q > 0 ? q : 1);
    }, 0);
  };

  const loadImageDimensions = (src, index) => {
    if (!src) return;
    const image = new Image();
    image.onload = () => {
      setImageDimensions((prev) => ({
        ...prev,
        [index]: {
          width: image.naturalWidth,
          height: image.naturalHeight,
        },
      }));
    };
    image.src = src;
  };

  useEffect(() => {
    if (selectedOrder?.images?.length) {
      selectedOrder.images.forEach((img, i) => {
        const src = typeof img === "string" ? img : img?.url || "";
        loadImageDimensions(src, i);
      });
    }
  }, [selectedOrder]);

  const handleOpenDetails = (order) => {
    setSelectedOrder(order);
  };

  const handleCloseDetails = () => {
    setSelectedOrder(null);
  };

  // Mount Stripe card element and Payment Request Button when drawer opens
  useEffect(() => {
    const mountCard = async () => {
      const stripe = await stripePromise;
      const elements = stripe.elements();
      window.stripeInstance = stripe;
      window.stripeElements = elements;

      const pr = stripe.paymentRequest({
        country: "US",
        currency: "usd",
        total: {
          label: "Order Total",
          amount: selectedOrder?.total
            ? Math.round(selectedOrder.total * 100)
            : 1000,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      const result = await pr.canMakePayment();
      if (result) {
        setPaymentRequest(pr);
        setCanMakePayment(true);
        const paymentRequestElement = elements.create("paymentRequestButton", {
          paymentRequest: pr,
          style: {
            paymentRequestButton: {
              type: "default",
              theme: "light",
              height: "40px",
            },
          },
        });
        paymentRequestElement.mount("#payment-request-button");
      } else {
        setCanMakePayment(false);
      }

      const cardNumber = elements.create("cardNumber");
      const cardExpiry = elements.create("cardExpiry");
      const cardCvc = elements.create("cardCvc");
      const postalCode = elements.create("postalCode");

      cardNumber.mount("#card-number");
      cardExpiry.mount("#card-expiry");
      cardCvc.mount("#card-cvc");
      postalCode.mount("#card-zip");

      setCardMounted(true);
    };

    if (showStripeCheckout && !cardMounted) {
      mountCard();
    }
  }, [showStripeCheckout, cardMounted, selectedOrder]);

  const handlePaymentSuccess = async () => {
    setShowStripeCheckout(false);

    setSelectedOrder((prev) => (prev ? { ...prev, status: "Paid" } : prev));

    try {
      if (!selectedOrder?.id) return;
      const orderRef = doc(db, "artOrders", selectedOrder.id);
      await updateDoc(orderRef, { status: "Paid" });
      // eslint-disable-next-line no-console
      console.log(`Order ${selectedOrder.id} marked as Paid in Firestore`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to update order status in Firestore:", err);
    }
  };

  const filteredOrders = useMemo(() => {
    const term = String(searchTerm || "")
      .trim()
      .toLowerCase();
    const status = String(statusFilter || "").trim();
    const items = orders
      .filter((order) => {
        const id = String(order?.id || "");
        const matchesId = !term || id.toLowerCase().includes(term);
        const matchesStatus = !status || String(order?.status || "") === status;
        return matchesId && matchesStatus;
      })
      .sort((a, b) => {
        const aDate = toDateSafe(a?.createdAt) || new Date(0);
        const bDate = toDateSafe(b?.createdAt) || new Date(0);
        return sortOption === "desc" ? bDate - aDate : aDate - bDate;
      });
    return items;
  }, [orders, searchTerm, statusFilter, sortOption]);

  useEffect(() => {
    setPageIndex(0);
  }, [searchTerm, statusFilter, sortOption, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / pageSize));
  const safePageIndex = Math.min(Math.max(0, pageIndex), totalPages - 1);
  const pagedOrders = useMemo(() => {
    const start = safePageIndex * pageSize;
    return filteredOrders.slice(start, start + pageSize);
  }, [filteredOrders, pageSize, safePageIndex]);

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setSortOption("desc");
    setPageIndex(0);
  };

  return (
    <div className="cp-orders">
      <div className="cp-orders__toolbar">
        <div className="cp-orders__toolbarTitle">
          <div className="cp-orders__sectionTitle">Find your art requests</div>
          <div className="cp-orders__sectionSub">
            Search, filter, and open an art request to view uploaded artwork and
            responses.
          </div>
        </div>

        <div className="cp-orders__controls">
          <div className="cp-orders__searchRow">
            <input
              className="cp-orders__input"
              type="text"
              placeholder="Search by request ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="cp-orders__advancedControls">
              <select
                className="cp-orders__select cp-orders__control--advanced"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All statuses</option>
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Due">Due</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>

              <select
                className="cp-orders__select cp-orders__control--advanced"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
              >
                <option value="desc">Newest first</option>
                <option value="asc">Oldest first</option>
              </select>

              <button
                type="button"
                className="cp-orders__controlBtn cp-orders__control--advanced"
                onClick={clearFilters}
              >
                Clear
              </button>
            </div>

            <button
              type="button"
              className="cp-orders__filtersIconBtn"
              onClick={() => setFiltersOpen(true)}
              aria-label="Filters"
              title="Filters"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
                <path
                  d="M3 6h18M6 12h12M10 18h4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {filtersOpen ? (
        <div
          className="cp-orders__filtersOverlay"
          role="dialog"
          aria-modal="true"
          aria-label="Art request filters"
          onClick={() => setFiltersOpen(false)}
        >
          <div
            className="cp-orders__filtersSheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cp-orders__filtersHeader">
              <div className="cp-orders__filtersTitle">Filters</div>
              <button
                type="button"
                className="op-btn op-btn--ghost"
                onClick={() => setFiltersOpen(false)}
              >
                Done
              </button>
            </div>
            <div className="cp-orders__filtersBody">
              <select
                className="cp-orders__select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All statuses</option>
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Due">Due</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>

              <select
                className="cp-orders__select"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
              >
                <option value="desc">Newest first</option>
                <option value="asc">Oldest first</option>
              </select>

              <button
                type="button"
                className="cp-orders__controlBtn"
                onClick={clearFilters}
              >
                Clear filters
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="cp-orders__layout">
        <div className="cp-orders__panel">
          <div className="cp-orders__panelHeader">
            <div className="cp-orders__panelTitle">Art Requests</div>
            <div className="cp-orders__panelHint">
              Click a row to view details
            </div>
          </div>

          <div className="cp-orders__list" role="list">
            {pagedOrders.length ? (
              pagedOrders.map((order) => {
                const id = String(order?.id || "");
                const shortId = id ? id.slice(0, 8) : "—";
                const imgs = getOrderImages(order);
                const uploadsCount = imgs.length;
                const totalQty = sumQuantities(imgs);
                const active =
                  selectedOrder &&
                  String(selectedOrder.id) === String(order.id);
                const status = String(order?.status || "Pending");

                return (
                  <button
                    key={id}
                    type="button"
                    className={`cp-orders__row ${active ? "is-active" : ""}`}
                    onClick={() => handleOpenDetails(order)}
                  >
                    <div className="cp-orders__rowTL">
                      <div className="cp-orders__orderId">#{shortId}</div>
                    </div>

                    <div className="cp-orders__rowTR">
                      <div
                        className={`cp-orders__statusCard cp-orders__status--${statusKey(
                          status
                        )}`}
                      >
                        {status}
                      </div>
                    </div>

                    <div className="cp-orders__rowBL">
                      <div className="cp-orders__rowBottom">
                        <span>{formatDateTime(order?.createdAt)}</span>
                        {uploadsCount ? (
                          <>
                            <span>•</span>
                            <span>
                              {uploadsCount} uploads
                              {totalQty && totalQty !== uploadsCount
                                ? ` (${totalQty} qty)`
                                : ""}
                            </span>
                          </>
                        ) : null}
                      </div>
                    </div>

                    <div className="cp-orders__rowBR">
                      <div className="cp-orders__total">
                        {formatMoney(order?.total)}
                      </div>
                      <div className="cp-orders__chev" aria-hidden="true">
                        ›
                      </div>
                    </div>
                  </button>
                );
              })
            ) : (
              <div style={{ padding: 12 }}>
                <div className="cp-orders__empty">
                  No art requests match your filters.
                </div>
              </div>
            )}
          </div>

          {filteredOrders.length > pageSize ? (
            <div
              className="cp-orders__pagination"
              role="navigation"
              aria-label="Art requests pagination"
            >
              <div className="cp-orders__paginationLeft">
                <span className="cp-orders__paginationText">
                  Page {safePageIndex + 1} of {totalPages}
                </span>
                <span className="cp-orders__paginationText">
                  Showing{" "}
                  {Math.min(
                    filteredOrders.length,
                    safePageIndex * pageSize + 1
                  )}
                  –
                  {Math.min(
                    filteredOrders.length,
                    (safePageIndex + 1) * pageSize
                  )}{" "}
                  of {filteredOrders.length}
                </span>
              </div>

              <div className="cp-orders__paginationRight">
                <label className="cp-orders__paginationText">
                  Per page{" "}
                  <select
                    className="cp-orders__select cp-orders__select--sm"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </label>

                <button
                  type="button"
                  className="op-btn op-btn--ghost"
                  onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                  disabled={safePageIndex <= 0}
                >
                  Prev
                </button>
                <button
                  type="button"
                  className="op-btn op-btn--ghost"
                  onClick={() =>
                    setPageIndex((p) => Math.min(totalPages - 1, p + 1))
                  }
                  disabled={safePageIndex >= totalPages - 1}
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div
          className={`cp-orders__detail ${selectedOrder ? "is-open" : ""}`}
          aria-live="polite"
          role={selectedOrder ? "dialog" : undefined}
          aria-modal={selectedOrder ? "true" : undefined}
        >
          {selectedOrder ? (
            <>
              <div className="cp-orders__detailHeader">
                <button
                  type="button"
                  className="cp-orders__backBtn"
                  onClick={handleCloseDetails}
                >
                  <span className="cp-orders__backArrow" aria-hidden="true">
                    ←
                  </span>
                  BACK
                </button>
                <div className="cp-orders__detailHeaderCenter">
                  <div className="cp-orders__detailTitle">
                    REQUEST {String(selectedOrder.id || "").slice(0, 8)}
                  </div>
                  <div className="cp-orders__detailSub">
                    {formatDateTime(selectedOrder?.createdAt)}
                  </div>
                </div>
                <div className="cp-orders__detailHeaderRight">
                  <span
                    className={`cp-orders__statusDetail cp-orders__status--${statusKey(
                      selectedOrder?.status
                    )}`}
                  >
                    {String(selectedOrder?.status || "Pending")}
                  </span>
                </div>
              </div>

              <div className="cp-orders__detailBody">
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 4,
                    mb: 4,
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      textAlign="left"
                      gutterBottom
                    >
                      Request ID
                    </Typography>
                    <Typography
                      fontWeight={500}
                      fontSize="1.1rem"
                      textAlign="left"
                    >
                      {selectedOrder.id}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        textAlign="left"
                        gutterBottom
                      >
                        Created
                      </Typography>
                      <Typography fontSize="0.95rem" textAlign="left">
                        {formatDateTime(selectedOrder?.createdAt)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        textAlign="left"
                        gutterBottom
                      >
                        Total Amount
                      </Typography>
                      <Typography
                        fontSize="1.1rem"
                        fontWeight={600}
                        color="#2e7d32"
                      >
                        {typeof selectedOrder.total === "number"
                          ? formatMoney(selectedOrder.total)
                          : "Pending"}
                      </Typography>
                      {selectedOrder.status === "Due" && (
                        <Button
                          onClick={() => setShowStripeCheckout(true)}
                          style={{
                            padding: 2,
                            marginTop: 10,
                            backgroundColor: "#94C940",
                            minWidth: "5rem",
                            color: "white",
                            borderRadius: "8px",
                            fontWeight: "1000",
                            fontFamily: "Anek Latin",
                            fontSize: "1.1rem",
                            textTransform: "uppercase",
                          }}
                        >
                          pay
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {selectedOrder.images?.map((img, i) => {
                    return (
                      <Paper
                        key={i}
                        elevation={1}
                        sx={{
                          p: 2,
                          background: "#f9f9f9",
                          borderRadius: 2,
                          borderLeft: "5px solid #94C940",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          setPreviewImage(
                            typeof img === "string" ? img : img?.url || ""
                          )
                        }
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <img
                            src={typeof img === "string" ? img : img?.url || ""}
                            alt={img?.name || `Image ${i + 1}`}
                            style={{
                              width: 60,
                              height: 60,
                              objectFit: "cover",
                              borderRadius: 4,
                              border: "1px solid #ccc",
                            }}
                          />
                          <Box>
                            <Typography fontWeight={600}>
                              {img?.name || `Image ${i + 1}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Size:
                              {imageDimensions[i]
                                ? `${(imageDimensions[i].width / 75).toFixed(
                                    2
                                  )}" × ${(
                                    imageDimensions[i].height / 75
                                  ).toFixed(2)}"`
                                : "..."}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Quantity: {img?.quantity || img?.count || 1}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>

                {selectedOrder.status === "Completed" && (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {selectedOrder.response?.map((img, i) => {
                      return (
                        <Paper
                          key={i}
                          elevation={1}
                          sx={{
                            p: 2,
                            background: "#f9f9f9",
                            borderRadius: 2,
                            borderLeft: "5px solid #94C940",
                            cursor: "pointer",
                          }}
                          onClick={() => setPreviewImage(img.url)}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <img
                              src={img.url}
                              alt={img.name || `Image ${i + 1}`}
                              style={{
                                backgroundImage: `
      linear-gradient(45deg, #ccc 21%, transparent 25%),
      linear-gradient(-45deg, #ccc 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #ccc 75%),
      linear-gradient(-45deg, transparent 75%, #ccc 75%)
    `,
                                backgroundSize: "20px 20px",
                                backgroundPosition:
                                  "0 0, 0 10px, 10px -10px, -10px 0px",
                                width: 100,
                                height: "auto",
                                objectFit: "cover",
                                borderRadius: 4,
                                border: "1px solid #ccc",
                              }}
                            />
                            <Box
                              style={{
                                flexGrow: 1,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                textAlign="left"
                                gutterBottom
                              >
                                Name
                              </Typography>
                              <Typography
                                fontWeight={500}
                                textAlign="left"
                                color="#5A6872"
                              >
                                {img.name || `Image ${i + 1}`}
                              </Typography>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                textAlign="left"
                                gutterBottom
                                mt={1}
                              >
                                Instructions
                              </Typography>
                              <Typography
                                fontWeight={500}
                                variant="body2"
                                color="#5A6872"
                                textAlign="left"
                              >
                                {img.instructions || 1}
                              </Typography>
                            </Box>
                            <button
                              type="button"
                              style={{
                                padding: "0.5rem 1rem",
                              }}
                              onClick={() => {
                                fetch(img.url)
                                  .then((response) => response.blob())
                                  .then((blob) => {
                                    const url =
                                      window.URL.createObjectURL(blob);
                                    const link = document.createElement("a");
                                    link.href = url;
                                    link.download = `${img.name}`;
                                    link.style.display = "none";
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    window.URL.revokeObjectURL(img.url);
                                  });
                              }}
                            >
                              Download
                            </button>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                )}
              </div>
            </>
          ) : (
            <div style={{ padding: 12 }}>
              <div className="cp-orders__empty">
                Select an art request to see its uploaded artwork, totals, and
                responses.
              </div>
            </div>
          )}
        </div>
      </div>

      {previewImage && (
        <dialog
          open={Boolean(previewImage)}
          style={{
            maxHeight: "90vh",
            width: "auto",
            display: "block",
            margin: "0 auto",
            borderRadius: "8px",
            background: previewBg,
          }}
        >
          <img
            src={previewImage}
            alt="Preview"
            style={{
              maxHeight: "90vh",
              width: "auto",
              display: "block",
              margin: "0 auto",
              borderRadius: "8px",
              background: "none",
              backgroundColor: "transparent",
            }}
          />

          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "center",
              marginBottom: "0.5rem",
              height: "2rem",
            }}
          >
            <button
              onClick={() => setPreviewBg("white")}
              style={{
                background: "white",
                color: "black",
                border: "hidden",
                fontWeight: 1000,
                fontFamily: "Anek Latin",
              }}
            >
              White BG
            </button>
            <button
              onClick={() => setPreviewBg("black")}
              style={{
                background: "black",
                color: "white",
                border: "hidden",
                fontWeight: 1000,
                fontFamily: "Anek Latin",
              }}
            >
              Black BG
            </button>
            <button
              onClick={() =>
                setPreviewBg(
                  `repeating-conic-gradient(#ccc 0% 25%, white 0% 50%) 50% / 20px 20px`
                )
              }
              style={{
                background:
                  "repeating-conic-gradient(#ccc 0% 25%, white 0% 50%) 50% / 20px 20px",
                border: "hidden",
                color: "black",
                fontWeight: 1000,
                fontFamily: "Anek Latin",
              }}
            >
              Checkered BG
            </button>
          </div>

          <button onClick={() => setPreviewImage(null)}>Close</button>
        </dialog>
      )}

      {showStripeCheckout && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "400px",
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 10px rgba(0,0,0,0.1)",
            transform: showStripeCheckout
              ? "translateX(0)"
              : "translateX(100%)",
            transition: "transform 0.3s ease-in-out",
            zIndex: 1300,
            padding: 3,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Stripe Checkout
            </Typography>
            <IconButton onClick={() => setShowStripeCheckout(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="body1" mb={2}>
            Enter your card information to pay{" "}
            <strong>
              {typeof selectedOrder?.total === "number"
                ? formatMoney(selectedOrder.total)
                : "$0.00"}
            </strong>
          </Typography>
          {canMakePayment && (
            <div
              id="payment-request-button"
              style={{ marginBottom: "1.5rem" }}
            ></div>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
            <Box
              id="card-number"
              sx={{ border: "1px solid #ccc", borderRadius: 1, p: 1 }}
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Box
                id="card-expiry"
                sx={{
                  flex: 1,
                  border: "1px solid #ccc",
                  borderRadius: 1,
                  p: 1,
                }}
              />
              <Box
                id="card-cvc"
                sx={{
                  flex: 1,
                  border: "1px solid #ccc",
                  borderRadius: 1,
                  p: 1,
                }}
              />
            </Box>
            <Box
              id="card-zip"
              sx={{ border: "1px solid #ccc", borderRadius: 1, p: 1 }}
            />
          </Box>
          {cardError && (
            <Typography color="error" variant="body2" mb={1}>
              {cardError}
            </Typography>
          )}
          <Button
            fullWidth
            variant="contained"
            style={{
              padding: 2,
              marginTop: 10,
              backgroundColor: "#94C940",
              minWidth: "5rem",
              color: "white",
              borderRadius: "8px",
              fontWeight: "1000",
              fontFamily: "Anek Latin",
              fontSize: "1.1rem",
              textTransform: "uppercase",
            }}
            onClick={async () => {
              setCardError(null);
              const stripe = window.stripeInstance;
              const elements = window.stripeElements;
              if (!stripe || !elements) {
                setCardError("Stripe has not loaded yet.");
                return;
              }
              const cardElement = elements.getElement("cardNumber");

              const { paymentMethod, error } = await stripe.createPaymentMethod(
                {
                  type: "card",
                  card: cardElement,
                }
              );

              if (error) {
                setCardError(error.message);
                return;
              }

              try {
                const currentUser = auth.currentUser;
                const token =
                  !STRIPE_TEST_MODE && currentUser
                    ? await currentUser.getIdToken(true)
                    : null;
                if (!STRIPE_TEST_MODE && !token) {
                  setCardError("You must be signed in to complete payment.");
                  return;
                }
                const amountCents = Math.round(
                  Math.max(0, Number(selectedOrder?.total || 0)) * 100
                );

                if (!PAYMENT_INTENT_ENDPOINTS.length) {
                  setCardError(
                    "Payment service not configured. Please contact support."
                  );
                  return;
                }

                let data = null;
                let lastError = null;
                for (const endpoint of PAYMENT_INTENT_ENDPOINTS) {
                  if (
                    !token &&
                    !isEmulatorEndpoint(endpoint) &&
                    STRIPE_TEST_MODE
                  ) {
                    continue;
                  }
                  try {
                    const response = await fetch(endpoint, {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        ...(STRIPE_TEST_MODE && isEmulatorEndpoint(endpoint)
                          ? { "X-Stripe-Test-Mode": "true" }
                          : {}),
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                      },
                      body: JSON.stringify({ amount: amountCents }),
                    });

                    if (!response.ok) {
                      const text = await response.text();
                      throw new Error(text || `HTTP ${response.status}`);
                    }

                    data = await response.json();
                    if (data?.error) {
                      throw new Error(data.error);
                    }
                    break;
                  } catch (err) {
                    lastError = err;
                    // eslint-disable-next-line no-console
                    console.warn(
                      "Payment intent request failed for",
                      endpoint,
                      err
                    );
                  }
                }

                if (!data) {
                  setCardError(
                    lastError?.message ||
                      "Unable to reach payment service. Please try again."
                  );
                  return;
                }

                const confirmResult = await stripe.confirmCardPayment(
                  data.clientSecret,
                  {
                    payment_method: paymentMethod.id,
                  }
                );

                if (confirmResult.error) {
                  setCardError(confirmResult.error.message);
                } else if (confirmResult.paymentIntent.status === "succeeded") {
                  // eslint-disable-next-line no-console
                  console.log(
                    "Payment succeeded:",
                    confirmResult.paymentIntent.id
                  );
                  handlePaymentSuccess();
                }
              } catch (err) {
                setCardError("Failed to create payment intent.");
                // eslint-disable-next-line no-console
                console.error(err);
              }
            }}
          >
            Submit Payment
          </Button>
        </Box>
      )}
    </div>
  );
}
